import api from '@Api/Api'
import { useSnackbar } from '@eduact/ed-components'
import { isApiResponse } from '@Features/Base/BaseApi/BaseApi'
import { isBaseError } from '@Features/Base/Error/BaseError'
import { handleUnauthorized } from '@Features/Base/BaseApi/handleUnauthorized'
import { SnackbarConfig } from '@Utilities/SnackbarConfig'
import React, { useEffect } from 'react'
import { useAppDispatch } from '@Hooks/ReduxHooks'

/**
 * Wires snackbars and auth failures onto the raw axios instance.
 *
 * The request interceptor that used to live here is gone. It was a near-copy of
 * the one in Api.ts — a second place attaching credentials, registered inside a
 * useEffect keyed on `openSnackbar`, so it re-registered every time that
 * identity changed and stacked duplicate interceptors on the same client. There
 * is now exactly one place that builds request headers.
 */
const ApiProvider: React.FC = ({ children }) => {
	const { openSnackbar } = useSnackbar()
	const dispatch = useAppDispatch()

	useEffect(() => {
		const responseInterceptor = api.interceptors.response.use(
			function (response) {
				const { data } = response
				if (isApiResponse(data) && data.show) {
					openSnackbar({ ...SnackbarConfig.success, text: data.message })
				}
				return Promise.resolve(response)
			},
			function (error) {
				// The ~6 raw-axios call sites bypassed the RTK interceptor entirely,
				// so a 401 on any of them — including the exam attachment upload —
				// was invisible. They share the same handler now.
				if (error?.status === 401 || error?.response?.status === 401) {
					handleUnauthorized(dispatch, error?.response ?? error)
					return Promise.reject(error.response ?? error)
				}
				if (isBaseError(error) && error.data.show) {
					openSnackbar({
						...SnackbarConfig.warning,
						text: error.data.message,
					})
				}
				return Promise.reject(error.response)
			}
		)

		// Eject on cleanup: without this, every re-render added another
		// interceptor to the same axios instance and none were ever removed.
		return () => {
			api.interceptors.response.eject(responseInterceptor)
		}
	}, [openSnackbar, dispatch])

	return <>{children}</>
}

export default ApiProvider
